<template>
  <div class="container-flexben" v-if="isAdmin">
    <!-- Page Header -->
    <div class="page-header">
      <div class="row">
        <div class="col-md-8">
          <h1 class="page-title">{{ $t("flexben.temporaryReopening.page_title") }}</h1>
        </div>
        <div class="col-md-4 text-right">
        </div>
      </div>
    </div>

    <div class="page-content">
      <div class="container-fluid">
        <!-- Filters Container - Separate from List -->
        <div class="container-bg-white">
          <div class="row p-3 align-items-end">
            <div class="col-12 col-xl-3 mb-2 mb-xl-0">
              <img src="@/assets/images/filter.svg"/>
              <span class="ml-3">{{ $t("flexben.general.filter") }}</span>
            </div>

            <!-- Company Filter (for Admin only) -->
            <div class="col-12 col-md-4 col-xl-3 mb-0" v-if="isAdmin">
              <div class="form-group mb-1">
                <label class="col-form-label pt-xl-0" for="company">
                  {{ $t("flexben.general.client") }}
                </label>
                <multiselect
                  id="company"
                  v-model="filters.company"
                  :options="filter_options.companies"
                  :searchable="true"
                  :close-on-select="true"
                  :show-labels="false"
                  :placeholder="$t('flexben.general.client')"
                  label="name"
                  track-by="id"
                  :selected-label="''"
                  :deselect-label="''"
                  :option-height="35"
                  :multiple="false"
                  :allow-empty="true"
                  ref="multiselect_companies"
                >
                  <template slot="singleLabel" slot-scope="{ option }">
                    {{ option.name }}
                  </template>
                </multiselect>
              </div>
            </div>

            <!-- recently_modified Filter -->
            <div class="col-12 col-md-4 col-xl-3 mb-0">
              <div class="form-group mb-1">
                <label class="col-form-label" :class="{'pt-xl-0': !isAdmin}" for="recently_modified">
                  {{ $t("flexben.temporaryReopening.filter_recently_modified") }}
                </label>
                <select id="recently_modified" class="form-control w-100" v-model="filters.recently_modified">
                  <option :value="null">{{ $t("flexben.general.all") }}</option>
                  <option :value="false">{{ $t("flexben.general.no") }}</option>
                  <option :value="true">{{ $t("flexben.general.yes") }}</option>
                </select>
              </div>
            </div>

            <!-- Action Button -->
            <div class="col-12 col-md-4 col-xl-3 ml-auto text-right">
              <button
                class="btn btn-themed "
                @click="openReopeningModal"
                :disabled="selectedItems.ids.length === 0"
              >
                {{ $t("flexben.temporaryReopening.btn.add_reopening") }}
              </button>
            </div>
          </div>
        </div>

        <!-- List Container - Separate from Filters -->
        <div class="container-bg-white mt-3">
          <div class="row p-3">
            <!-- List Header with Search -->
            <div class="col-6 text-bold">
              <h3 class="p-0 mb-0 mt-2">
                {{ $t("flexben.temporaryReopening.table.beneficiary_list") }}
              </h3>
            </div>
            <div class="col-6">
              <input
                type="text"
                @keyup.enter="search()"
                v-model="searchString"
                :placeholder="$t('flexben.general.search')"
                class="formedituser search-input float-right"
              />
            </div>

            <!-- Selected Items Alert -->
            <div class="col-12  ml-2 pt-2" v-if="selectedItems.ids.length > 0">
              <div class="alert alert-info">
                {{ selectedItemsMessage }}
                <button class="btn btn-themed ml-2 " @click="resetSelection()">
                  {{ $t("flexben.temporaryReopening.clear_selection") }}
                </button>

                <!-- Separator and Delete Exceptions Section -->
                <template v-if="allSelectedHaveActiveAccess">
                  <hr class="my-3">
                  <div class="d-flex align-items-center justify-content-between">
                    <span class="font-weight-bold">
                      {{ $t("flexben.temporaryReopening.delete_exceptions_text") }}
                    </span>
                    <button
                      class="btn btn-danger"
                      @click="deleteExceptions()"
                      :disabled="loading">
                      <font-awesome-icon icon="trash" class="mr-2" />
                      {{ $t("flexben.temporaryReopening.btn.delete_exceptions") }}
                    </button>
                  </div>
                </template>
              </div>
            </div>

            <!-- Table -->
            <div class="col-12 mt-3">
              <div class="table-responsive hover-table">
                <table class="table table-striped">
                  <thead>
                    <tr>
                      <td> </td>
                      <td>{{ $t("flexben.temporaryReopening.table.beneficiary_name") }}</td>
                      <td v-if="isAdmin">{{ $t("flexben.general.client") }}</td>
                      <td>{{ $t("flexben.temporaryReopening.table.beneficiary_email") }}</td>
                      <td>{{ $t("flexben.temporaryReopening.table.beneficiary_phone") }}</td>
                      <td>{{ $t("flexben.temporaryReopening.table.beneficiary_cnp") }}</td>
                      <td>{{ $t("flexben.temporaryReopening.table.beneficiary_status") }}</td>
                      <td>{{ $t("flexben.temporaryReopening.table.beneficiary_recently_modified") }}</td>
                      <td>{{ $t("flexben.temporaryReopening.table.temporary_ignore_timesheet") }}</td>
                      <td></td>
                    </tr>
                  </thead>
                  <tbody v-if="!loading && filteredReopenings.length > 0">
                  <tr class="trhover" v-for="(item, index) in filteredReopenings" :key="index">
                    <td class="w-50-p">
                      <input
                        type="checkbox"
                        class="form-check"
                        @click="checkItem(item.id)"
                        :checked="selectedItems.ids.includes(item.id)"
                        :disabled="selectedCompanyId === null"
                      />
                    </td>
                    <td>{{ item.beneficiary_name }}</td>
                    <td v-if="isAdmin">{{ item.company_name }}</td>
                    <td>{{ item.beneficiary_email }}</td>
                    <td>{{ item.beneficiary_phone }}</td>
                    <td>{{ item.cnp }}</td>
                    <td>{{ $t("flexben.temporaryReopening.table.status_"+ item.beneficiary_status)  }}</td>
                    <td>
                      <!-- Active temporary access -->
                      <span
                        v-if="item.has_temporary_access && item.temporary_access_is_active"
                        class="badge badge-pill badge-success px-3 py-2"
                        style="font-size: 13px; white-space: nowrap;"
                        v-html="formatDateRange(item.temporary_access_started_at, item.temporary_access_expires_at)">
                      </span>
                      <span
                        v-else-if="item.has_temporary_access && !item.temporary_access_is_active"
                        class="badge badge-secondary "
                        style="font-size: 13px;white-space: nowrap;">
                        {{ $t("flexben.temporaryReopening.table.no_recently_modified") }}
                      </span>
                      <span v-else class="text-muted">-</span>
                    </td>
                    <td>
                      <span
                        v-if="item.has_temporary_access && item.temporary_access_is_active && item.temporary_ignore_timesheet"
                        class="badge badge-pill badge-success px-3 py-2"
                        style="font-size: 13px; white-space: nowrap;">
                        {{ $t("flexben.temporaryReopening.table.recently_modified") }}
                      </span>
                      <span
                        v-else
                        class="badge badge-secondary "
                        style="font-size: 13px;white-space: nowrap;">
                        {{ $t("flexben.temporaryReopening.table.no_recently_modified") }}
                      </span>
                    </td>
                    <td></td>
                  </tr>
                  </tbody>
                  <tbody v-else>
                  <tr class="gri-cell text-center trhover">
                    <td :colspan="isAdmin ? 9 : 8" class="text-center">
                      <div v-if="isAdmin && !selectedCompanyId" class="py-4">
                        <img src="@/assets/images/info-sign.svg" class="mb-3" alt="info" />
                        <p class="font-weight-bold mb-2">
                          {{ $t("flexben.temporaryReopening.table.select_company_first") }}
                        </p>
                        <p class="text-muted">
                          {{ $t("flexben.temporaryReopening.table.select_company_hint") }}
                        </p>
                      </div>
                      <div v-else>
                        {{ $t("flexben.general.no_results_found") }}
                      </div>
                    </td>
                  </tr>
                  </tbody>
                </table>
              </div>
            </div>

            <!-- Pagination -->
            <div class="col-12 p-0">
              <listPagination
                :currentPage="reopenings.current_page"
                :totalItems="reopenings.total"
                :startItem="reopenings.from"
                :endItem="reopenings.to"
                :itemsPerPage="Number(reopenings.perPage)"
                @pageChanged="pageChanged"
                @changePerPage="changePerPage"
              ></listPagination>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Modal for Creating Reopening -->
    <TemporaryReopeningModal
      ref = "reopeningModal"
      :selectedUsers="getSelectedUsers()"
      :companyId="selectedCompanyId"
      @save="onModalSave"
     open-modal/>

     <content-loader :loaderVisible="loading" :loaderError="loaderError"></content-loader>
  </div>
</template>

<script>
import httpFlexbenService from "@/http/http-flexben.js";
import TemporaryReopeningModal from "./modals/TemporaryReopeningModal";
import Multiselect from 'vue-multiselect';
import moment from "moment/moment";

export default {
  name: "TemporaryReopeningIndex",
  components: {
    TemporaryReopeningModal,
    Multiselect,
  },
  data() {
    return {
      httpService: httpFlexbenService.temporaryReopening,
      loading: true,
      loaderError: false,
      searchString: '',
      isAdmin: false,
      selectedCompanyId: null,

      filters: {
        sort_by: null,
        sort_direction: null,
        page: null,
        search: null,
        company: null,
        recently_modified: null,
        perPage: null,
      },
      filter_options: {
        company_id: null,
        companies: [],
      },
      reopenings: {
        data: [],
        current_page: 1,
        from: 1,
        to: 1,
        perPage: "20",
        lastPage: 1,
        total: 0
      },
      selectedItems: {
        ids: [],
      },
    }
  },
  watch: {
    'filters.company'(newVal) {
      if (newVal) {
        this.selectedCompanyId = newVal.id || newVal;
      }
      this.applyFilterAndReload();
    },

    'filters.recently_modified'() {
      this.applyFilterAndReload();
    }
  },
  created() {
    // Check user role
    let userRole = this.flxUserLevel().userLevel;
    const employment = this.$store.getters.flx_activeEmployment;

    if (userRole === 'client' && employment != null) {
      this.filters.company = employment.company_id;
      this.selectedCompanyId = employment.company_id;
      this.isAdmin = false;
      this._loadList();
    } else if (userRole === 'admin') {
      this.isAdmin = true;
      this._loadFilterOptions();
    } else {
      window.location.href = '/';
    }
  },
  methods: {
    formatDateRange(startDate, endDate) {
      const formatDate = (dateString) => {
        if (!dateString) return '';

        const date = new Date(dateString);
        const day = String(date.getDate()).padStart(2, '0');
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const year = date.getFullYear();
        const hours = String(date.getHours()).padStart(2, '0');
        const minutes = String(date.getMinutes()).padStart(2, '0');

        return `${day}.${month}.${year} ${hours}:${minutes}`;
      };

      return `${formatDate(startDate)} <br>- ${formatDate(endDate)}`;
    },
    getSelectedUsers() {
      return this.reopenings.data.filter(item =>
        this.selectedItems.ids.includes(item.id)
      );
    },
    checkItem(itemId) {
      const index = this.selectedItems.ids.indexOf(itemId);

      if (index > -1) {
        this.selectedItems.ids.splice(index, 1);
      } else {
        this.selectedItems.ids.push(itemId);
      }
    },
    resetSelection() {
      this.selectedItems.ids = [];
    },
    _loadFilterOptions() {
      this.loading = true;

      this.httpService.getFilters()
        .then((response) => response.data)
        .then((data) => {
          this.filter_options.companies = data.filters.company_id;
          this.loading = false;
        })
        .catch((error) => {
          console.error('Error loading filters:', error);
          this.filter_options.companies = [];
          this.loading = false;

          if (error.response && error.response.status >= 500) {
            this.loaderError = true;
          }
        });

    },
    _loadList() {
      this.loading = true;
      if( !this.filters.company) {
        this.selectedCompanyId = null;
        this.loading = false;
        this.reopenings = {
          data: [],
          current_page: 1,
          from: 0,
          to: 0,
          perPage: 20,
          lastPage: 1,
          total: 0
        };
        return;
      }
      let companyId = this.filters.company;
      if (this.isAdmin && this.filters.company && this.filters.company.id) {
        companyId = this.filters.company.id;
        this.selectedCompanyId = companyId;
      }

      const payload = {
        company_id: companyId,
        search: this.filters.search,
        active_only: this.filters.recently_modified ? 1 : 0 ,
        order_by: this.filters.sort_by,
        sorted_by: this.filters.sort_direction,
        limit: this.filters.perPage,
        page: this.filters.page,
        per_page: this.filters.perPage,
      };
      this.httpService.list(payload)
        .then((response) => response.data)
        .then((data) => {
          console.log('$data: ',data)
          this.reopenings = {
            data: data.data.map(item => {
              const companyEmployee = item.company_employee || {};
              const company = item.company || {};
              const exception = item.exception || null;

              return {
                id: companyEmployee.id,
                beneficiary_name: companyEmployee.full_name || '-',
                beneficiary_email: companyEmployee.email || '-',
                beneficiary_phone: companyEmployee.phone_number || '-',
                cnp: companyEmployee.cnp || '-',
                beneficiary_status: companyEmployee.status || '-',
                company_name: company.name || '-',
                company_id: company.id || null,
                // Temporary access details
                has_temporary_access: exception !== null,
                temporary_access_expires_at: exception ? exception.end_at : null,
                temporary_access_started_at: exception ? exception.start_at : null,
                temporary_access_is_active: exception ? exception.is_active : false,
                temporary_ignore_timesheet: exception ? exception.ignore_timesheet : false,
              };
            }),
            current_page: data.meta.current_page,
            from: data.meta.from,
            to: data.meta.to,
            perPage: data.meta.per_page,
            lastPage: data.meta.last_page,
            total: data.meta.total
          };
          this.loading = false;
        })
        .catch((error) => {
          console.error('Error loading list:', error);

          this.reopenings = {
            data: [],
            current_page: 1,
            from: 0,
            to: 0,
            perPage: 20,
            lastPage: 1,
            total: 0
          };

          if (error.response && error.response.status >= 500) {
            this.loaderError = true;
          }
          this.loading = false;
        });
    },
    search() {
      if (this.searchString.length >= 3) {
        this.filters.search = this.searchString;
      } else if (this.searchString.length === 0) {
        this.filters.search = null;
      }
      this.applyFilterAndReload();
    },
    changePerPage(perPage) {
      this.filters.page = null;
      this.filters.perPage = perPage;
      this._loadList();
    },
    pageChanged(pageNb) {
      this.filters.page = pageNb;
      this._loadList();
    },
    openReopeningModal() {
      if (this.selectedItems.ids.length === 0) {
        alert(this.$t("flexben.temporaryReopening.modal.validation.no_users"));
        return;
      }
      // Set selectedCompanyId if admin and company filter is set
      if (this.isAdmin && this.filters.company) {
        this.selectedCompanyId = this.filters.company.id || this.filters.company;
      }
      this.$refs.reopeningModal.openModal();
    },

    applyFilterAndReload() {
      this.loading = true;
      this.filters.page = 1;
      this.resetSelection();
      this._loadList();
    },

    _sendTemporaryAccessRequest(payload, action) {
      this.loading = true;
      this.httpService.store(payload)
        .then(() => {
          const message = action === 'save'
            ? this.$t("flexben.temporaryReopening.toast.success_message")
            : this.$t("flexben.temporaryReopening.toast.delete_success");

          this.$bvToast.toast(message, {
            title: this.$t("flexben.general.status_type.success"),
            solid: true,
            autoHideDelay: 3000,
            toaster: 'b-toaster-bottom-center',
            variant: 'success'
          });

          this.resetSelection();
          this._loadList();
          if (action === 'save') {
            this.$refs.reopeningModal.closeModal();
          }
        })
        .catch((error) => {
            const errorMessage = (error.response && error.response.data && error.response.data.message)
              || this.$t("flexben.general.error_occurred");

            this.$bvToast.toast(errorMessage, {
              title: this.$t("flexben.general.error"),
              solid: true,
              autoHideDelay: 3000,
              toaster: 'b-toaster-bottom-center',
              variant: 'danger'
            })
        })
        .finally(() => {
          this.loading = false;
          if (this.$refs.reopeningModal) {
            this.$refs.reopeningModal.loading = false;
          }
        });
    },
    onModalSave(payload) {
      console.log('Modal save payload:', payload);
      this._sendTemporaryAccessRequest(payload,'save');
    },
    deleteExceptions() {
      this._sendTemporaryAccessRequest({
        end_at: null,
        start_at: moment().format("YYYY-MM-DD HH:mm:ss"),
        company_employee_ids: this.selectedItems.ids
      },'delete',)
    }
  },
  computed : {
    filteredReopenings() {
      if (this.filters.recently_modified === false) {
        return this.reopenings.data.filter(item =>
          !item.has_temporary_access || !item.temporary_access_is_active
        );
      }
      return this.reopenings.data;
    },
    selectedItemsMessage() {
      const count = this.selectedItems.ids.length;

      if (count === 1) {
        return this.$t('flexben.temporaryReopening.one_user_selected', { count });
      } else if (count > 1 && count < 20) {
        return this.$t('flexben.temporaryReopening.few_users_selected', { count });
      } else {
        return this.$t('flexben.temporaryReopening.many_users_selected', { count });
      }
    },
    allSelectedHaveActiveAccess() {
      if (this.selectedItems.ids.length === 0) {
        return false;
      }

      const selectedUsers = this.reopenings.data.filter(item =>
        this.selectedItems.ids.includes(item.id)
      );

      return selectedUsers.every(user =>
        user.has_temporary_access && user.temporary_access_is_active
      );
    }
  }
};
</script>
